import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PlanLimitProvider } from "./context/PlanLimitContext";
import { ThemeProvider } from "./context/ThemeContext";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { SubscriptionGate } from "./components/SubscriptionGate";
import ImpersonationBanner from "./components/ImpersonationBanner";
import CookieBanner from "./components/CookieBanner";
import { installImpersonationInterceptor } from "./lib/impersonation";

// Phase 19.15: install the impersonation axios interceptor before any auth
// requests fire. Idempotent.
installImpersonationInterceptor();

// Layout
import Layout from "./components/layout/Layout";

// Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Employees from "./pages/Employees";
import EmployeeDetail from "./pages/EmployeeDetail";
import Companies from "./pages/Companies";
import CompanyDetail from "./pages/CompanyDetail";
import ClientsAndCompanies from "./pages/ClientsAndCompanies";
import ClientDetail from "./pages/ClientDetail";
import ServicesAndGoods from "./pages/ServicesAndGoods";
import QuotesWithMassUpdate from "./pages/QuotesWithMassUpdate";
import QuoteForm from "./pages/QuoteForm";
import QuoteDetail from "./pages/QuoteDetail";
import SafetyDocuments from "./pages/SafetyDocuments";
import Analytics from "./pages/Analytics";
import FleetManagement from "./pages/FleetManagement";
import EquipmentManagement from "./pages/EquipmentManagement";
import ResourceUsageTimeline from "./pages/ResourceUsageTimeline";
import Invoices from "./pages/Invoices";
import CalendarPage from "./pages/Calendar";
import PortalSettings from "./pages/PortalSettings";
import Payroll from "./pages/Payroll";
import PayStubs from "./pages/PayStubs";
import Pensions from "./pages/Pensions";
import AccountSettings from "./pages/AccountSettings";
import MyProfile from "./pages/MyProfile";
import BusinessProfile from "./pages/BusinessProfile";
import Policies from "./pages/Policies";
import Unions from "./pages/Unions";
import DocumentManagement from "./pages/DocumentManagement";
import AccountsReceivable from "./pages/AccountsReceivable";
import AccountsPayable from "./pages/AccountsPayable";
import ComplianceAlerts from "./pages/ComplianceAlerts";
import TaxDocuments from "./pages/TaxDocuments";
import CategoryManagement from "./pages/CategoryManagement";
import DocumentTemplates from "./pages/DocumentTemplates";
import CertificationManagement from "./pages/CertificationManagement";
import Training from "./pages/Training";
import TermsConditions from "./pages/TermsConditions";
import LegalDocument from "./pages/LegalDocument";
import AdminLegalEditor from "./pages/AdminLegalEditor";
import AdminCommunications from "./pages/AdminCommunications";
import PortalCommunications from "./pages/PortalCommunications";
import UniversalTemplates from "./pages/UniversalTemplates";
import UploadAndExtract from "./pages/UploadAndExtract";
import OnboardingWizard from "./pages/OnboardingWizard";
import LandingPage from "./pages/LandingPage";
import AuditLogs from "./pages/AuditLogs";
import Onboarding from "./pages/Onboarding";
import PerformanceDevelopment from "./pages/PerformanceDevelopment";
import Benefits from "./pages/Benefits";
import BankingCredit from "./pages/BankingCredit";
import ROEs from "./pages/ROEs";
import PayrollSummaries from "./pages/PayrollSummaries";
import TaxCompliance from "./pages/TaxCompliance";
import ClientPortalManagement from "./pages/ClientPortalManagement";
import InventoryAdmin from "./pages/InventoryAdmin";
import PartsProcurement from "./pages/PartsProcurement";
import CustomizationCenter from "./pages/CustomizationCenter";
import PlatformTemplateAdmin from "./pages/PlatformTemplateAdmin";
import Expenses from "./pages/Expenses";
import OperationalDocs from "./pages/OperationalDocs";
import TimeClock from "./pages/TimeClock";
import PayEngine from "./pages/PayEngine";
import PriceLists from "./pages/PriceLists";
import Memberships from "./pages/Memberships";
import FleetAssetTracker from "./pages/FleetAssetTracker";
import JobBudgets from "./pages/JobBudgets";
import NotificationSettings from "./pages/NotificationSettings";
import Recruitment from "./pages/Recruitment";
import JobPhotos from "./pages/JobPhotos";
import AccessRequests from "./pages/AccessRequests";
import AuditYoY from "./pages/AuditYoY";
import PublicCatalog from "./pages/PublicCatalog";
import TeamChat from "./pages/TeamChat";
import PdfTemplates from "./pages/PdfTemplates";
import AccountingModule, {
  AcctChartOfAccounts, AcctJournalEntries, AcctGeneralLedger,
  AcctFinancialReports, AcctVendors, AcctVendorBills, AcctCheques,
  AcctBankAccounts, AcctBankReconciliation, AcctBranches, AcctBulkUpload,
  AcctHealthScore,
} from "./pages/AccountingModule";
import Coupons from "./pages/Coupons";
import Marketing from "./pages/Marketing";
import Localization from "./pages/Localization";
import PendingAcknowledgments from "./pages/PendingAcknowledgments";

// Subscription/SaaS Pages
import Pricing from "./pages/Pricing";
import Signup from "./pages/Signup";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import ManageSubscription from "./pages/ManageSubscription";

// Auth Pages
import VerifyEmail from "./pages/VerifyEmail";
import ResendVerification from "./pages/ResendVerification";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";

// Admin Pages
import AdminUserManagement from "./pages/AdminUserManagement";

// User Profile
import ProfileSettings from "./pages/ProfileSettings";
import VerifyEmailChange from "./pages/VerifyEmailChange";

// Module Configuration
import ModuleConfiguration from "./pages/ModuleConfiguration";
import BillingSubscription from "./pages/BillingSubscription";
import PostsAndInquiriesAdmin from "./pages/PostsAndInquiriesAdmin";
import EmailTemplates from "./pages/EmailTemplates";
import QuoteInvoiceUpload from "./pages/QuoteInvoiceUpload";
import BulkUpload from "./pages/BulkUpload";
import QuoteEditor from "./pages/QuoteEditor";
import NewQuoteFromTemplate from "./pages/NewQuoteFromTemplate";

// Admin Pages
import AdminDashboard from "./pages/AdminDashboard";

// Portal Pages
import ClientDashboard from "./pages/portals/ClientDashboard";
import EmployeeDashboard from "./pages/portals/EmployeeDashboard";
import PortalSignLanding from "./pages/PortalSignLanding";
import NetworkErrorBanner from "./components/NetworkErrorBanner";
import ErrorBoundary from "./components/ErrorBoundary";
import AdminComms from "./pages/AdminComms";

// Public Payment Pages (no auth required - accessed from email links)
import PublicInvoice from "./pages/public/PublicInvoice";
import PublicQuote from "./pages/public/PublicQuote";
import PublicReceipt from "./pages/public/PublicReceipt";
import PaymentSuccess from "./pages/public/PaymentSuccess";

// Smart redirect component based on account type
const SmartRedirect = () => {
  const { user, loading } = useAuth();
  
  if (loading) return null;
  
  if (!user) return <Navigate to="/login" replace />;
  
  // Redirect based on account type
  if (user.account_type === "client_portal") {
    return <Navigate to="/client-portal" replace />;
  }
  if (user.account_type === "employee_portal") {
    return <Navigate to="/employee-portal" replace />;
  }
  
  // Default for internal users (office_manager, quoter, employee)
  return <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <ErrorBoundary>
    <ThemeProvider>
      <AuthProvider>
        <SubscriptionProvider>
          <BrowserRouter>
            <PlanLimitProvider>
            <NetworkErrorBanner />
            <ImpersonationBanner />
            <CookieBanner />
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/landing" element={<LandingPage />} />
              
              {/* Auth verification & password reset routes */}
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/verify-email-change" element={<VerifyEmailChange />} />
              <Route path="/resend-verification" element={<ResendVerification />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              
              {/* SaaS/Subscription routes */}
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/subscription/success" element={<SubscriptionSuccess />} />
              <Route path="/subscription/canceled" element={<Navigate to="/manage-subscription" replace />} />
              <Route path="/manage-subscription" element={<ManageSubscription />} />

              {/* Public legal pages (Terms / Privacy / …) */}
              <Route path="/legal/:slug" element={<LegalDocument />} />
              <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
              <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />

              {/* Public payment routes - no auth required (accessed from email links) */}
              <Route path="/pay/invoice/:invoiceId" element={<PublicInvoice />} />
              <Route path="/pay/quote/:quoteId" element={<PublicQuote />} />
              <Route path="/pay/receipt/:invoiceId" element={<PublicReceipt />} />
              <Route path="/pay/success" element={<PaymentSuccess />} />

              {/* Portal routes - standalone layouts */}
              <Route path="/client-portal" element={<ClientDashboard />} />
              <Route path="/employee-portal" element={<EmployeeDashboard />} />
              {/* Phase F.6 Batch D+ — Magic-link landing (auto-login from doc email) */}
              <Route path="/portal/sign" element={<PortalSignLanding />} />

            {/* Protected routes for internal users - require active subscription */}
            <Route path="/" element={<SubscriptionGate><Layout /></SubscriptionGate>}>
              <Route index element={<SmartRedirect />} />
              <Route path="dashboard" element={<Dashboard />} />
              
              {/* Feature-gated routes */}
              <Route path="employees" element={<ProtectedRoute feature="employees"><Employees /></ProtectedRoute>} />
              <Route path="employees/:employeeId" element={<ProtectedRoute feature="employees"><EmployeeDetail /></ProtectedRoute>} />
              <Route path="companies" element={<Companies />} />
              <Route path="companies/:companyId" element={<CompanyDetail />} />
              <Route path="clients" element={<ProtectedRoute feature="clients"><ClientsAndCompanies /></ProtectedRoute>} />
              <Route path="clients/:clientId" element={<ProtectedRoute feature="clients"><ClientDetail /></ProtectedRoute>} />
              <Route path="services" element={<ProtectedRoute feature="services_goods"><ServicesAndGoods /></ProtectedRoute>} />
              <Route path="quotes" element={<QuotesWithMassUpdate />} />
              <Route path="quotes/upload" element={<QuoteInvoiceUpload docType="quote" />} />
              <Route path="bulk-upload" element={<BulkUpload />} />
              <Route path="quotes/new-from-template" element={<NewQuoteFromTemplate />} />
              <Route path="quotes/:id/editor" element={<QuoteEditor />} />
              <Route path="quotes/new" element={<QuoteForm />} />
              <Route path="quotes/:id" element={<QuoteDetail />} />
              <Route path="quotes/:id/edit" element={<QuoteForm />} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="schedule" element={<Navigate to="/calendar" replace />} />
              <Route path="weekly-summary" element={<Navigate to="/calendar?tab=weekly-summary" replace />} />
              <Route path="safety-documents" element={<ProtectedRoute feature="safety"><SafetyDocuments /></ProtectedRoute>} />
              <Route path="documents" element={<ProtectedRoute feature="documents"><DocumentManagement /></ProtectedRoute>} />
              <Route path="analytics" element={<ProtectedRoute feature="basic_reports"><Analytics /></ProtectedRoute>} />
              <Route path="compliance-alerts" element={<ProtectedRoute feature="basic_reports"><ComplianceAlerts /></ProtectedRoute>} />
              <Route path="invoices" element={<ProtectedRoute feature="invoicing"><Invoices /></ProtectedRoute>} />
              <Route path="invoices/upload" element={<ProtectedRoute feature="invoicing"><QuoteInvoiceUpload docType="invoice" /></ProtectedRoute>} />
              <Route path="accounts-receivable" element={<ProtectedRoute feature="invoicing"><AccountsReceivable /></ProtectedRoute>} />
              <Route path="accounts-payable" element={<ProtectedRoute feature="invoicing"><AccountsPayable /></ProtectedRoute>} />
              <Route path="payroll" element={<ProtectedRoute feature="payroll"><Payroll /></ProtectedRoute>} />
              <Route path="pay-stubs" element={<ProtectedRoute feature="payroll"><PayStubs /></ProtectedRoute>} />
              <Route path="tax-documents" element={<ProtectedRoute feature="tax_settings"><TaxDocuments /></ProtectedRoute>} />
              <Route path="fleet" element={<ProtectedRoute feature="fleet"><FleetManagement /></ProtectedRoute>} />
              <Route path="equipment" element={<ProtectedRoute feature="equipment"><EquipmentManagement /></ProtectedRoute>} />
              <Route path="policies" element={<ProtectedRoute feature="policies"><Policies /></ProtectedRoute>} />
              <Route path="union" element={<ProtectedRoute feature="unions"><Unions /></ProtectedRoute>} />
              <Route path="unions" element={<Navigate to="/union" replace />} />
              <Route path="portal-settings" element={<ProtectedRoute feature="portal_management"><PortalSettings /></ProtectedRoute>} />
              <Route path="posts-and-inquiries" element={<ProtectedRoute feature="portal_management"><PostsAndInquiriesAdmin /></ProtectedRoute>} />
              <Route path="business-profile" element={<BusinessProfile />} />
              <Route path="categories" element={<CategoryManagement />} />
              <Route path="document-templates" element={<ProtectedRoute feature="documents"><DocumentTemplates /></ProtectedRoute>} />
              <Route path="certifications" element={<ProtectedRoute feature="certifications"><CertificationManagement /></ProtectedRoute>} />
              <Route path="training" element={<ProtectedRoute feature="training"><Training /></ProtectedRoute>} />
              <Route path="onboarding" element={<ProtectedRoute feature="employees"><Onboarding /></ProtectedRoute>} />
              <Route path="performance" element={<ProtectedRoute feature="employees"><PerformanceDevelopment /></ProtectedRoute>} />
              <Route path="benefits" element={<ProtectedRoute feature="employees"><Benefits /></ProtectedRoute>} />
              <Route path="pensions" element={<ProtectedRoute feature="employees"><Pensions /></ProtectedRoute>} />
              <Route path="account" element={<AccountSettings />} />
              <Route path="my-profile" element={<MyProfile />} />
              <Route path="roes" element={<ProtectedRoute feature="payroll"><ROEs /></ProtectedRoute>} />
              <Route path="payroll-summaries" element={<ProtectedRoute feature="payroll"><PayrollSummaries /></ProtectedRoute>} />
              <Route path="banking-credit" element={<ProtectedRoute feature="invoicing"><BankingCredit /></ProtectedRoute>} />
              <Route path="tax-compliance" element={<ProtectedRoute feature="tax_settings"><TaxCompliance /></ProtectedRoute>} />
              <Route path="client-portal-management" element={<ProtectedRoute feature="clients"><ClientPortalManagement /></ProtectedRoute>} />
              <Route path="terms-conditions" element={<TermsConditions />} />
              <Route path="audit-logs" element={<ProtectedRoute feature="audit_logs"><AuditLogs /></ProtectedRoute>} />
              <Route path="admin" element={<AdminDashboard />} />
              <Route path="admin/legal" element={<AdminLegalEditor />} />
              <Route path="admin/communications" element={<AdminCommunications />} />
              <Route path="portal/communications" element={<PortalCommunications />} />
              <Route path="universal-templates" element={<UniversalTemplates />} />
              <Route path="upload-extract" element={<UploadAndExtract />} />
              <Route path="onboarding-wizard" element={<OnboardingWizard />} />
              <Route path="admin/users" element={<AdminUserManagement />} />
              <Route path="profile" element={<ProfileSettings />} />
              <Route path="billing" element={<BillingSubscription />} />
              <Route path="modules" element={<ModuleConfiguration />} />
              <Route path="email-templates" element={<EmailTemplates />} />
              <Route path="inventory" element={<ProtectedRoute feature="services_goods"><InventoryAdmin /></ProtectedRoute>} />
              <Route path="parts-procurement" element={<PartsProcurement />} />
              <Route path="customization" element={<CustomizationCenter />} />
              <Route path="platform/templates" element={<PlatformTemplateAdmin />} />
              <Route path="admin/comms" element={<AdminComms />} />
              <Route path="expenses" element={<Expenses />} />
              <Route path="operational-docs" element={<OperationalDocs />} />
              <Route path="time-clock" element={<TimeClock />} />
              <Route path="pay-engine" element={<PayEngine />} />
              <Route path="price-lists" element={<PriceLists />} />
              <Route path="memberships" element={<Memberships />} />
              <Route path="fleet-asset-tracker" element={<FleetAssetTracker />} />
              <Route path="job-budgets" element={<JobBudgets />} />
              <Route path="notification-settings" element={<NotificationSettings />} />
              <Route path="recruitment" element={<Recruitment />} />
              <Route path="job-photos" element={<JobPhotos />} />
              <Route path="access-requests" element={<AccessRequests />} />
              <Route path="audit-yoy" element={<AuditYoY />} />
              <Route path="public-catalog" element={<PublicCatalog />} />
              <Route path="team-chat" element={<TeamChat />} />
              <Route path="pdf-templates" element={<PdfTemplates />} />
              <Route path="coupons" element={<Coupons />} />
              <Route path="marketing" element={<Marketing />} />
              <Route path="localization" element={<Localization />} />
              <Route path="admin/pending-acknowledgments" element={<PendingAcknowledgments />} />
              {/* iter150 — Accounting Pro (CA) — Under Construction shells */}
              <Route path="accounting" element={<AccountingModule />}>
                <Route path="chart-of-accounts" element={<AcctChartOfAccounts />} />
                <Route path="journal-entries" element={<AcctJournalEntries />} />
                <Route path="general-ledger" element={<AcctGeneralLedger />} />
                <Route path="reports" element={<AcctFinancialReports />} />
                <Route path="vendors" element={<AcctVendors />} />
                <Route path="vendor-bills" element={<AcctVendorBills />} />
                <Route path="cheques" element={<AcctCheques />} />
                <Route path="bank-accounts" element={<AcctBankAccounts />} />
                <Route path="reconciliation" element={<AcctBankReconciliation />} />
                <Route path="branches" element={<AcctBranches />} />
                <Route path="bulk-upload" element={<AcctBulkUpload />} />
                <Route path="health-score" element={<AcctHealthScore />} />
              </Route>
            </Route>

            {/* Fallback - smart redirect */}
            <Route path="*" element={<SmartRedirect />} />
          </Routes>
          </PlanLimitProvider>
        </BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
        </SubscriptionProvider>
      </AuthProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
